import { Injectable, computed, inject, signal } from '@angular/core';
import { Subscription, forkJoin, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { BusinessService } from '../../services/business/business.service';
import { ScheduleService } from '../../services/business/schedule.service';
import { AuthService } from '../../services/auth';

export type OnboardingChecklist = {
  hasProfessionals: boolean;
  hasServices: boolean;
  hasSchedule: boolean;
};

const defaultChecklist: OnboardingChecklist = {
  hasProfessionals: false,
  hasServices: false,
  hasSchedule: false,
};

@Injectable({ providedIn: 'root' })
export class OnboardingService {
  private businessService = inject(BusinessService);
  private scheduleService = inject(ScheduleService);
  private authService = inject(AuthService);

  private checklistState = signal<OnboardingChecklist>(defaultChecklist);
  private loadingState = signal(false);
  private hasFetched = signal(false);
  /** Business the current checklist state belongs to */
  private fetchedBusinessId: string | null = null;
  private pendingRequest?: Subscription;

  readonly status = this.checklistState.asReadonly();
  readonly isLoading = this.loadingState.asReadonly();

  /** All steps completed */
  readonly isComplete = computed(() => {
    const s = this.checklistState();
    return s.hasProfessionals && s.hasServices && s.hasSchedule;
  });

  /** Show onboarding when fetched and not yet complete */
  readonly shouldShowChecklist = computed(
    () => this.hasFetched() && !this.isComplete()
  );

  /** Progress percentage (0-100) */
  readonly progressPercent = computed(() => {
    const s = this.checklistState();
    const total = 3;
    let done = 0;
    if (s.hasProfessionals) done++;
    if (s.hasServices) done++;
    if (s.hasSchedule) done++;
    return Math.round((done / total) * 100);
  });

  /** Number of completed steps */
  readonly completedSteps = computed(() => {
    const s = this.checklistState();
    let done = 0;
    if (s.hasProfessionals) done++;
    if (s.hasServices) done++;
    if (s.hasSchedule) done++;
    return done;
  });

  readonly totalSteps = 3;

  /**
   * Loads the onboarding checklist from the backend.
   * The service is a root singleton that outlives logout/login, so the cached
   * state is only reused while it belongs to the same business; otherwise it is
   * discarded and re-fetched before the checklist is shown or hidden.
   */
  checkPrerequisites(force = false): void {
    const businessId = this.authService.getUserInfo()?.business_id ?? null;
    const businessChanged = businessId !== this.fetchedBusinessId;

    if (businessChanged) {
      this.reset();
    } else {
      if (this.loadingState()) return;
      if (this.hasFetched() && !force && this.isComplete()) return;
    }

    if (!businessId) {
      this.checklistState.set(defaultChecklist);
      this.hasFetched.set(true);
      return;
    }

    this.fetchedBusinessId = businessId;
    this.loadingState.set(true);
    this.pendingRequest = forkJoin({
      services: this.fetchHasAny(businessId, 'services'),
      professionals: this.fetchHasAny(businessId, 'professionals'),
      schedule: this.fetchHasSchedule(businessId),
    }).subscribe({
      next: ({ services, professionals, schedule }) => {
        this.checklistState.set({
          hasServices: services,
          hasProfessionals: professionals,
          hasSchedule: schedule,
        });
        this.loadingState.set(false);
        this.hasFetched.set(true);
      },
      error: () => {
        this.checklistState.set(defaultChecklist);
        this.loadingState.set(false);
        this.hasFetched.set(true);
      },
    });
  }

  /** Discards any cached checklist and cancels an in-flight request */
  reset(): void {
    this.pendingRequest?.unsubscribe();
    this.pendingRequest = undefined;
    this.fetchedBusinessId = null;
    this.checklistState.set(defaultChecklist);
    this.loadingState.set(false);
    this.hasFetched.set(false);
  }

  private fetchHasAny(
    businessId: string,
    endpoint: 'services' | 'professionals'
  ) {
    return this.businessService
      .getRecordsByBusinessId<any>(
        businessId,
        { page: 1, pageSize: 1, isActive: true },
        endpoint
      )
      .pipe(
        map((res) => this.extractCount(res, endpoint) > 0),
        catchError(() => of(false))
      );
  }

  private fetchHasSchedule(businessId: string) {
    return this.scheduleService.getBusinessSchedule(businessId).pipe(
      map((res) => {
        const activeRanges =
          res.timeRanges?.filter((range: any) => range.isActive) ?? [];
        return activeRanges.length > 0;
      }),
      catchError(() => of(false))
    );
  }

  private extractCount(res: any, prop: string): number {
    if (!res) return 0;
    if (Array.isArray(res[prop])) return res[prop].length;
    if (typeof res.totalCount === 'number') return res.totalCount;
    if (Array.isArray(res)) return res.length;
    return 0;
  }
}
